import type { AzulfyAuthUser } from "hooks/useUser";

const AUTH_PUBLIC_PREFIXES = ["/login", "/forget-password", "/reset-password"];
export const INTENTIONAL_LOGOUT_KEY = "azulfyIntentionalLogout";

export function consumeIntentionalLogout() {
  if (typeof window === "undefined") {
    return false;
  }

  const wasIntentional = sessionStorage.getItem(INTENTIONAL_LOGOUT_KEY) === "1";

  if (wasIntentional) {
    sessionStorage.removeItem(INTENTIONAL_LOGOUT_KEY);
  }

  return wasIntentional;
}

export function getStoredAuthUser() {
  if (typeof window === "undefined") {
    return null;
  }

  const storedAuthUser = localStorage.getItem("azulfyAuthUser");

  if (!storedAuthUser) {
    return null;
  }

  try {
    return JSON.parse(storedAuthUser) as AzulfyAuthUser;
  } catch {
    return null;
  }
}

export function getAuthenticatedHomePath(
  authUser: AzulfyAuthUser | null | undefined,
) {
  if (!authUser?.isAuthenticated) {
    return "/login";
  }

  if (authUser.roles === "super_admin") {
    return "/home";
  }

  if (
    authUser.roles === "external_service_provider" &&
    authUser.workspaces?.uuid &&
    authUser.support?.uuid
  ) {
    return `/workspace/${authUser.workspaces.uuid}/support/${authUser.support.uuid}`;
  }

  if (authUser.workspaces?.uuid) {
    const endDate = new Date().toISOString().split("T")[0];
    const startDate = new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .split("T")[0];

    return `/workspace/${authUser.workspaces.uuid}/home?startDate=${startDate}&endDate=${endDate}`;
  }

  return "/home";
}

function isAuthPublicPath(path: string) {
  const pathOnly = path.split("?")[0] ?? path;

  return AUTH_PUBLIC_PREFIXES.some(
    (route) => pathOnly === route || pathOnly.startsWith(`${route}/`),
  );
}

function isSafeInternalPath(path: string) {
  return path.startsWith("/") && !path.startsWith("//") && !path.includes("://");
}

export function getLoginPathWithRedirect(
  returnPath: string | null | undefined,
) {
  if (!returnPath || returnPath === "/" || !isSafeInternalPath(returnPath)) {
    return "/login";
  }

  if (isAuthPublicPath(returnPath)) {
    return "/login";
  }

  return `/login?redirect=${encodeURIComponent(returnPath)}`;
}

export function getSafePostLoginPath(
  redirect: string | null | undefined,
  authUser: AzulfyAuthUser | null | undefined,
) {
  const fallback = getAuthenticatedHomePath(authUser);

  if (!redirect || !isSafeInternalPath(redirect) || isAuthPublicPath(redirect)) {
    return fallback;
  }

  return redirect;
}

export function canAccessImpersonation(
  authUser: AzulfyAuthUser | null | undefined,
) {
  return Boolean(authUser?.isAuthenticated && authUser.roles === "super_admin");
}
